import { useLayoutEffect, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { recordWidgetDataReady } from 'hooks/dashboard/dashboardWidgetTimings'

interface DashboardWidgetSkeletonProps {
  minHeight?: number
  label?: string
}

export type WidgetTimingMeta = {
  id: string
  queryKeys?: string[]
  notes?: string
}

const timingMeta = (timing?: WidgetTimingMeta | string): WidgetTimingMeta | undefined =>
  typeof timing === 'string' ? { id: timing } : timing

/**
 * True once this widget's query is ready. Also records TTFD when `timing` is set.
 * Paint is immediate — no shared stagger. useLayoutEffect so hydrate marks fire on
 * first paint with numbers, not after the background revalidate finishes.
 */
export const useProgressiveReveal = (
  ready: boolean,
  enabled = true,
  timing?: WidgetTimingMeta | string,
): boolean => {
  const mountAtRef = useRef(typeof performance === 'undefined' ? 0 : performance.now())
  const recordedRef = useRef(false)
  const meta = timingMeta(timing)

  useLayoutEffect(() => {
    if (!enabled || !ready || !meta?.id || recordedRef.current) return
    recordedRef.current = true
    const fromCache = Boolean(
      (window as Window & { __OTW_DASHBOARD_CACHE_RESTORED__?: boolean }).__OTW_DASHBOARD_CACHE_RESTORED__,
    )
    recordWidgetDataReady(meta.id, {
      widgetMountMs: performance.now() - mountAtRef.current,
      queryKeys: meta.queryKeys,
      notes: meta.notes,
      fromCache,
    })
  }, [ready, enabled, meta?.id, meta?.notes, meta?.queryKeys])

  return ready
}

export const WidgetSlot: React.FC<{
  loading?: boolean
  minHeight?: number
  label?: string
  /** Kept for call-site compat; reveal is immediate when not loading. */
  progressive?: boolean
  timingId?: string
  children: React.ReactNode
}> = ({ loading = false, minHeight = 180, label = 'Loading', timingId, children }) => {
  const revealed = useProgressiveReveal(!loading, true, timingId)
  if (!revealed) {
    return <DashboardWidgetSkeleton minHeight={minHeight} label={label} />
  }
  return <>{children}</>
}

export const DashboardWidgetSkeleton: React.FC<DashboardWidgetSkeletonProps> = ({
  minHeight = 180,
  label = 'Loading',
}) => {
  const compact = minHeight <= 80
  return (
    <Box
      role='status'
      aria-label={label}
      sx={{
        width: '100%',
        height: minHeight,
        minHeight,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        bgcolor: 'background.paper',
        border: compact ? 0 : 1,
        borderColor: 'divider',
        borderRadius: compact ? 0 : 1,
      }}
    >
      <CircularProgress size={compact ? 16 : 22} aria-hidden />
      {compact ? null : (
        <Typography variant='caption' color='text.secondary'>
          {label}
        </Typography>
      )}
    </Box>
  )
}

export default DashboardWidgetSkeleton
